import { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { useAuth } from '@/lib/auth';
import { PageHeader } from '@/components/ui/layout';
import { Alert, Button } from '@/components/ui/primitives';
import { TicketListView } from './TicketListView';
import { TicketThread } from './TicketThread';
import { TENANT_DESK } from './desk-api';

/**
 * Support, from inside the hospital.
 *
 * Deliberately reachable when the subscription has lapsed and the app is
 * read-only: the hospital that cannot write is exactly the one that needs to
 * tell somebody. Internal notes between the account manager and the platform
 * are filtered out server-side, so nothing on this screen has to hide them.
 */
export function TenantSupportPage() {
  const { can } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [openId, setOpenId] = useState<string | null>(() => params.get('ticket'));

  if (openId) {
    return (
      <>
        <Button variant="ghost" className="mb-3" onClick={() => setOpenId(null)}>
          <ArrowLeft className="me-1.5 h-4 w-4" /> Back to tickets
        </Button>
        <TicketThread endpoints={TENANT_DESK} ticketId={openId} />
      </>
    );
  }

  return (
    <>
      <PageHeader
        title="Support"
        description="Raise an issue with the people who look after this system"
        actions={
          can('support.ticket.raise') && <Button onClick={() => navigate('/support/tickets/new')}>New ticket</Button>
        }
      />

      <Alert className="mb-4">
        Your account manager sees these first and answers most of them. Anything they cannot settle
        goes to the platform owner.
      </Alert>

      <TicketListView endpoints={TENANT_DESK} showTenant={false} onOpen={(row) => setOpenId(row.id)} />
    </>
  );
}
